import _objectSpread from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectSpread";
import _toConsumableArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/usr/src/app/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { contrastingTextColor } from '@jbrowse/core/util/color';
import { createJBrowseTheme } from '@jbrowse/core/ui';
import { observer } from 'mobx-react';
import { bpSpanPx, revcom, complement, defaultStarts, defaultStops, defaultCodonTable, generateCodonTable } from '@jbrowse/core/util';
function Translation(props) {
  var codonTable = props.codonTable,
    seq = props.seq,
    frame = props.frame,
    bpPerPx = props.bpPerPx,
    region = props.region,
    height = props.height,
    y = props.y,
    _props$reverse = props.reverse,
    reverse = _props$reverse === void 0 ? false : _props$reverse,
    theme = props.theme;
  var scale = bpPerPx;
  // the tilt variable normalizes the frame to where we are starting from,
  // which increases consistency across blocks
  var tilt = 3 - region.start % 3;
  // the effectiveFrame incorporates tilt and the frame to say what the
  // effective frame that is plotted. The +3 is for when frame is -2 and this
  // can otherwise result in effectiveFrame -1
  var effectiveFrame = (frame + tilt + 3) % 3;
  var seqSliced = seq.slice(effectiveFrame);
  var translated = [];
  for (var i = 0; i < seqSliced.length; i += 3) {
    var codon = seqSliced.slice(i, i + 3);
    var normalizedCodon = reverse ? revcom(codon) : codon;
    var aminoAcid = codonTable[normalizedCodon] || '';
    translated.push({
      letter: aminoAcid,
      codon: normalizedCodon.toUpperCase()
    });
  }
  var w = 1 / scale * 3;
  var drop = region.start === 0 ? 0 : w;
  var render = 1 / bpPerPx >= 12;
  var width = (region.end - region.start) / bpPerPx;
  var map = ['#d8d8d8', '#adadad', '#8f8f8f'].reverse();
  return React.createElement(React.Fragment, null, translated.map(function (element, index) {
    var x = region.reversed ? width - (w * (index + 1) + effectiveFrame / scale - drop) : w * index + effectiveFrame / scale - drop;
    var letter = element.letter,
      codon = element.codon;
    return React.createElement(React.Fragment, {
      key: "".concat(index, "-").concat(letter)
    }, React.createElement("rect", {
      x: x,
      y: y,
      width: render ? w : w + 0.7 /* small fudge factor when zoomed out*/,
      height: height,
      stroke: render ? '#555' : 'none',
      fill: defaultStarts.includes(codon) ? theme.palette.startCodon : defaultStops.includes(codon) ? theme.palette.stopCodon : map[Math.abs(frame)]
    }), render ? React.createElement("text", {
      x: x + w / 2,
      y: y + height / 2,
      dominantBaseline: "middle",
      textAnchor: "middle"
    }, letter) : null);
  }));
}
function DNA(props) {
  var bpPerPx = props.bpPerPx,
    region = props.region,
    feature = props.feature,
    theme = props.theme,
    height = props.height,
    seq = props.seq,
    y = props.y;
  var render = 1 / bpPerPx >= 12;
  var _bpSpanPx = bpSpanPx(feature.get('start'), feature.get('end'), region, bpPerPx),
    _bpSpanPx2 = _slicedToArray(_bpSpanPx, 2),
    leftPx = _bpSpanPx2[0],
    rightPx = _bpSpanPx2[1];
  var reverse = region.reversed;
  var len = feature.get('end') - feature.get('start');
  var w = Math.max((rightPx - leftPx) / len, 0.8);
  return React.createElement(React.Fragment, null, seq.split('').map(function (letter, index) {
    var color = theme.palette.bases[letter.toUpperCase()];
    var x = reverse ? rightPx - (index + 1) * w : leftPx + index * w;
    return React.createElement(React.Fragment, {
      key: index
    }, React.createElement("rect", {
      x: x,
      y: y,
      width: w,
      height: height,
      fill: color ? color.main : '#aaa',
      stroke: render ? '#555' : 'none'
    }), render ? React.createElement("text", {
      x: x + w / 2,
      y: y + height / 2,
      dominantBaseline: "middle",
      textAnchor: "middle",
      fill: color ? contrastingTextColor(color.main) : 'black'
    }, letter) : null);
  }));
}
var SequenceSVG = function SequenceSVG(_ref) {
  var regions = _ref.regions,
    configTheme = _ref.theme,
    _ref$features = _ref.features,
    features = _ref$features === void 0 ? new Map() : _ref$features,
    _ref$showReverse = _ref.showReverse,
    showReverse = _ref$showReverse === void 0 ? true : _ref$showReverse,
    _ref$showForward = _ref.showForward,
    showForward = _ref$showForward === void 0 ? true : _ref$showForward,
    _ref$showTranslation = _ref.showTranslation,
    showTranslation = _ref$showTranslation === void 0 ? true : _ref$showTranslation,
    bpPerPx = _ref.bpPerPx;
  var _regions = _slicedToArray(regions, 1),
    region = _regions[0];
  var theme = createJBrowseTheme(configTheme);
  var codonTable = generateCodonTable(defaultCodonTable);
  var height = 20;
  var _ref2 = _toConsumableArray(features.values()),
    feature = _ref2[0];
  if (!feature) {
    return null;
  }
  var seq = feature.get('seq');
  if (!seq) {
    return null;
  }
  // incrementer for the y-position of the current sequence being rendered
  // (applies to both translation rows and dna rows)
  var currY = -20;
  return React.createElement(React.Fragment, null, showTranslation && (region.reversed ? showReverse : showForward) ? [2, 1, 0].map(function (index) {
    return React.createElement(Translation, {
      key: "translation-".concat(index),
      seq: seq,
      y: currY += 20,
      codonTable: codonTable,
      frame: index,
      bpPerPx: bpPerPx,
      region: region,
      theme: theme,
      height: height,
      reverse: region.reversed
    });
  }) : null, showForward ? React.createElement(DNA, {
    height: height,
    y: currY += 20,
    feature: feature,
    region: region,
    seq: region.reversed ? complement(seq) : seq,
    bpPerPx: bpPerPx,
    theme: theme
  }) : null, showReverse ? React.createElement(DNA, {
    height: height,
    y: currY += 20,
    feature: feature,
    region: region,
    seq: region.reversed ? seq : complement(seq),
    bpPerPx: bpPerPx,
    theme: theme
  }) : null, showTranslation && (region.reversed ? showForward : showReverse) ? [0, -1, -2].map(function (index) {
    return React.createElement(Translation, {
      key: "rev-translation-".concat(index),
      seq: seq,
      y: currY += 20,
      codonTable: codonTable,
      frame: index,
      bpPerPx: bpPerPx,
      region: region,
      theme: theme,
      height: height,
      reverse: !region.reversed
    });
  }) : null);
};
var Wrapper = function Wrapper(_ref3) {
  var exportSVG = _ref3.exportSVG,
    width = _ref3.width,
    totalHeight = _ref3.totalHeight,
    children = _ref3.children;
  return exportSVG ? React.createElement(React.Fragment, null, children) : React.createElement("svg", {
    "data-testid": "sequence_track",
    width: width,
    height: totalHeight,
    style: {
      width: width,
      height: totalHeight
    }
  }, children);
};
function Sequence(props) {
  var regions = props.regions,
    bpPerPx = props.bpPerPx;
  var _regions2 = _slicedToArray(regions, 1),
    region = _regions2[0];
  var width = (region.end - region.start) / bpPerPx;
  var totalHeight = 200;
  return React.createElement(Wrapper, _objectSpread({}, props, {
    totalHeight: totalHeight,
    width: width
  }), React.createElement(SequenceSVG, _objectSpread({}, props)));
}
export default observer(Sequence);